import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pause, Play } from 'lucide-react';
import heroVideo from '@assets/Plasmics_Image_Video_1792501034655.mp4';
import posterImg from '@/assets/hero-sample-poster.jpg';
import heroImg from '@assets/plasmics-hero.png';
import './hero-sample.css';

const RM = '(prefers-reduced-motion: reduce)';

export default function HeroSample() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia(RM).matches);
  const [userPaused, setUserPaused] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visible, setVisible] = useState(true);
  const [tabShown, setTabShown] = useState(() => typeof document === 'undefined' || !document.hidden);
  const hero = useRef<HTMLElement>(null);
  const root = useRef<HTMLElement>(null);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const mq = window.matchMedia(RM);
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    const on = () => setTabShown(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  useEffect(() => {
    if (!hero.current) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0 });
    io.observe(hero.current);
    return () => io.disconnect();
  }, []);

  // Scroll zoom/parallax: event-driven, one rAF per scroll burst, fully reversible.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (reduced) {
      el.style.removeProperty('--hs-zoom');
      el.style.removeProperty('--hs-shift');
      return;
    }
    let raf = 0;
    const apply = () => {
      raf = 0;
      const h = hero.current?.offsetHeight || window.innerHeight;
      const p = Math.min(Math.max(window.scrollY / h, 0), 1);
      el.style.setProperty('--hs-zoom', (1 + p * 0.12).toFixed(4));
      el.style.setProperty('--hs-shift', `${(p * h * 0.22).toFixed(1)}px`);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduced]);

  const shouldPlay = !reduced && !failed && !userPaused && visible && tabShown;

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (!shouldPlay) { v.pause(); return; }
    let cancelled = false;
    v.muted = true;
    v.play().catch(() => {
      // Pausing or unmounting can reject an outstanding play request.
      // That is not a playback failure and must not discard the video.
      if (!cancelled) setFailed(true);
    });
    return () => { cancelled = true; };
  }, [shouldPlay, reduced]);

  const toggle = useCallback(() => {
    if (failed) { setFailed(false); setUserPaused(false); return; }
    setUserPaused(p => !p);
  }, [failed]);

  const showVideo = !reduced;
  const playing = !userPaused && !failed;
  useEffect(() => {
    if (reduced || failed) {
      setIsPlaying(false);
      setReady(false);
    }
  }, [reduced, failed]);

  const canControl = showVideo;

  return (
    <main ref={root} className="hero-sample text-foreground">
      <section ref={hero} className="hs-hero" data-testid="hero-sample-hero" data-playback={isPlaying ? 'playing' : 'paused'} data-reduced={reduced} aria-labelledby="hs-title">
        <div className="hs-media" aria-hidden="true" data-testid="hero-sample-media">
          <img src={posterImg} alt="" data-testid="hero-sample-poster" />
          {showVideo && !failed && (
            <video
              ref={video}
              className="hs-video"
              data-testid="hero-sample-video"
              data-ready={ready}
              data-playback={isPlaying ? 'playing' : 'paused'}
              src={heroVideo}
              poster={posterImg}
              muted
              loop
              playsInline
              preload="metadata"
              tabIndex={-1}
              onPlaying={() => { setReady(true); setIsPlaying(true); }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={() => setFailed(true)}
            />
          )}
        </div>
        <div className="hs-veil" aria-hidden="true" />
        <div className="hs-gear" aria-hidden="true">
          <img src={`${import.meta.env.BASE_URL}meshes/gear.svg`} alt="" />
        </div>
        <div className="container mx-auto hs-title">
          <h1 id="hs-title" className="text-5xl md:text-7xl lg:text-8xl font-sans font-light tracking-tight text-foreground leading-[1.03] max-w-3xl text-balance">
            End-to-End <span className="font-bold">Manufacturing Systems</span>
          </h1>
        </div>
        {canControl && (
          <button type="button" className="hs-ctl" onClick={toggle} aria-pressed={!playing} data-testid="button-hero-video-toggle">
            {failed ? <Play className="w-3.5 h-3.5" /> : playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {failed ? 'Retry video' : playing ? 'Pause video' : 'Play video'}
          </button>
        )}
      </section>

      <section className="hs-next py-24 md:py-32 border-y border-border" id="vision">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative aspect-square md:aspect-[4/3] bg-white border border-border p-4 shadow-sm">
              <div className="w-full h-full relative bg-muted overflow-hidden">
                <img src={heroImg} alt="Plasmics production cells" className="w-full h-full object-cover grayscale-[0.5] contrast-125 mix-blend-multiply" />
              </div>
            </div>
            <div className="flex flex-col gap-8">
              <div>
                <span className="font-mono text-primary text-xs font-bold tracking-[0.3em] uppercase block mb-4">The Plasmics Platform</span>
                <h2 className="text-4xl md:text-5xl font-light text-balance leading-tight">
                  A cohesive stack, <span className="font-bold">not isolated machines.</span>
                </h2>
              </div>
              <div className="space-y-6 text-muted-foreground text-lg font-light leading-relaxed">
                <p>We engineer comprehensive manufacturing platforms. By integrating proprietary hardware, real-time perception, and adaptive software into a single deployable cell, we deliver guaranteed reliability for decentralized production.</p>
                <p>Our customers do not just buy a printer—they deploy a <strong className="text-foreground font-bold">fully integrated production node</strong> capable of continuous, autonomous operation.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6 mt-4 border-t border-border pt-8">
                {['Full-Stack Integration', 'Active Thermal Environments', 'Autonomous ML Correction', 'Decentralized Fleet Operations'].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-sm border border-secondary flex items-center justify-center bg-secondary/10 mt-0.5 text-secondary"><Check className="w-3 h-3" /></div>
                    <span className="font-mono text-xs font-bold uppercase tracking-wide text-foreground leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
